/**
 * `/login/sso-error?reason=…` — the refusal pages of the platform sign-in (M0-F3b §7).
 *
 * One screen, one closed set of reasons. Nothing here leaks: no platform error string, no
 * e-mail address, no user id, and no hint about whether an account exists. The server already
 * reduced whatever happened to one of these codes and logged the real cause.
 *
 * It lives on the F3 auth shell, so a person who is turned away sees the same 64 px header and
 * 376 px column they just came from, not a browser error page.
 */
import { useSearchParams } from 'react-router';
import { AuthShell } from './AuthShell';

export interface SsoErrorCopy {
  title: string;
  body: string;
}

/** The reasons `oidc-routes.ts` can redirect with, and nothing else. */
export const SSO_ERRORS: Record<string, SsoErrorCopy> = {
  access_denied: {
    title: "Your platform account isn't allowed in yet",
    body: 'An Eli Z Platform administrator has to allow your account for Eli Z Monday. Ask them to add you, then try again.',
  },
  deactivated: {
    title: 'Your account is deactivated',
    body: 'Your Eli Z Monday account has been deactivated. Ask an administrator to reactivate it.',
  },
  not_invited: {
    title: "You don't have an Eli Z Monday account",
    body: 'Your platform sign-in worked, but there is no Eli Z Monday account for you yet. Ask an administrator to invite you.',
  },
  disabled: {
    title: 'Platform sign-in is turned off',
    body: 'Sign in with your e-mail and password, or ask an administrator to turn platform sign-in back on.',
  },
  expired: {
    title: 'That sign-in attempt expired',
    body: 'Sign-in attempts are only valid for a few minutes. Start again from the login page.',
  },
};

export const GENERIC_SSO_ERROR: SsoErrorCopy = {
  title: 'Something went wrong signing you in',
  body: "We couldn't complete the sign-in. Try again, and tell an administrator if it keeps happening.",
};

export function ssoErrorCopy(reason: string | null): SsoErrorCopy {
  return (reason && SSO_ERRORS[reason]) || GENERIC_SSO_ERROR;
}

export function SsoError() {
  const [params] = useSearchParams();
  const copy = ssoErrorCopy(params.get('reason'));
  return <SsoErrorView copy={copy} />;
}

/** Split out so the fixture harness can render each variant without a router query. */
export function SsoErrorView({ copy }: { copy: SsoErrorCopy }) {
  return (
    <AuthShell title={copy.title} testId="sso-error-page">
      <p className="ez-auth__panel" data-testid="sso-error-body">
        {copy.body}
      </p>
      <p className="ez-auth__links">
        <a className="ez-auth__link" href="/login" data-testid="sso-error-back">
          Back to login
        </a>
      </p>
    </AuthShell>
  );
}
